import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, MapPin, Phone, Mail, ArrowLeft } from "lucide-react";
import { getPousada, getPousadaFotos } from "@/lib/publicApi";
import { FotoGallery } from "@/components/public/FotoGallery";
import { ChipsComodidades } from "@/components/public/ChipsComodidades";
import { QuartoCard } from "@/components/public/QuartoCard";
import { CTAWhatsApp } from "@/components/public/CTAWhatsApp";
import { brl } from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function PousadaDetail() {
  const { slug } = useParams();
  const [pousada, setPousada] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const data = await getPousada(slug);
        if (cancel) return;
        setPousada(data);
        const f = await getPousadaFotos(data.id).catch(() => []);
        if (cancel) return;
        setFotos(f);
      } catch (err) {
        if (!cancel) {
          if (err?.response?.status === 404) setNotFound(true);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  const precoDesde = useMemo(() => {
    if (!pousada?.quartos) return null;
    const vals = pousada.quartos.map((q) => Number(q.valor_diaria || 0)).filter((v) => v > 0);
    return vals.length ? Math.min(...vals) : null;
  }, [pousada]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 space-y-6">
        <div className="h-5 w-48 bg-stone-200/60 rounded animate-pulse" />
        <div className="aspect-[16/10] bg-stone-200/60 rounded-lg animate-pulse" />
        <div className="h-10 w-2/3 bg-stone-200/60 rounded animate-pulse" />
        <div className="h-24 bg-stone-200/60 rounded animate-pulse" />
      </div>
    );
  }

  if (notFound || !pousada) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">404</div>
        <h1 className="font-public-title text-3xl text-stone-900 mb-4">Pousada não encontrada</h1>
        <p className="text-stone-600 mb-8">O endereço buscado pode ter sido alterado ou removido.</p>
        <Link to="/pousadas" data-testid="pousada-404-back" className="inline-flex items-center gap-2 text-sm text-[#083A6B] underline">
          <ArrowLeft size={14} /> Ver todas as pousadas
        </Link>
      </div>
    );
  }

  const quartos = Array.isArray(pousada.quartos) ? pousada.quartos : [];
  const ctxWa = { type: "pousada", nome: pousada.nome };

  return (
    <>
      <Helmet>
        <title>{`${pousada.nome} · ${BRAND.name}`}</title>
        <meta name="description" content={pousada.descricao_curta || `Conheça ${pousada.nome} na ${BRAND.name}.`} />
        <link rel="canonical" href={`/pousadas/${pousada.slug || pousada.id}`} />
        <meta property="og:title" content={`${pousada.nome} · ${BRAND.name}`} />
        <meta property="og:description" content={pousada.descricao_curta || pousada.nome} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="max-w-7xl mx-auto px-5 lg:px-10 pt-6 lg:pt-8">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500">
          <li><Link to="/" className="hover:text-[#083A6B]">Início</Link></li>
          <li><ChevronRight size={12} /></li>
          <li><Link to="/pousadas" className="hover:text-[#083A6B]">Pousadas</Link></li>
          <li><ChevronRight size={12} /></li>
          <li className="text-stone-700 truncate max-w-[220px] sm:max-w-none">{pousada.nome}</li>
        </ol>
      </nav>

      {/* Header + Galeria */}
      <section data-testid="pousada-detail" className="max-w-7xl mx-auto px-5 lg:px-10 py-6 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-3">
            <FotoGallery fotos={fotos} alt={pousada.nome} testid="pousada-gallery" />
          </div>
          <div className="lg:col-span-2 flex flex-col gap-6">
            {pousada.is_chacara && (
              <span className="self-start text-[10px] uppercase tracking-[0.2em] bg-[#D4A652] text-white px-2.5 py-1 rounded-full">
                Chácara
              </span>
            )}
            <h1 className="font-public-title text-3xl lg:text-4xl text-stone-900 leading-tight">
              {pousada.nome}
            </h1>
            {pousada.descricao_curta && (
              <p className="text-stone-600 text-base leading-relaxed">
                {pousada.descricao_curta}
              </p>
            )}
            {pousada.endereco && (
              <div className="flex items-start gap-2 text-sm text-stone-600">
                <MapPin size={14} className="mt-1 flex-shrink-0 text-[#083A6B]" />
                <span>{pousada.endereco}</span>
              </div>
            )}

            <div className="rounded-lg border border-stone-200 bg-[#FFFFFF] p-5 space-y-4">
              {precoDesde != null && precoDesde > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Diárias a partir de</div>
                  <div className="font-public-title text-3xl text-[#083A6B]">{brl(precoDesde)}</div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <CTAWhatsApp
                  variant="primary"
                  context={ctxWa}
                  label="Reservar via WhatsApp"
                  testid="pousada-cta-whatsapp"
                />
                <a
                  href="#quartos"
                  data-testid="pousada-cta-quartos"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-[#083A6B]/20 text-[#083A6B] hover:bg-[#083A6B]/5 text-sm font-medium transition-colors"
                >
                  Ver quartos disponíveis
                </a>
              </div>
              <div className="pt-3 border-t border-stone-200 space-y-2 text-xs text-stone-600">
                {pousada.telefone && (
                  <a href={`tel:${pousada.telefone.replace(/\D/g, "")}`} className="flex items-center gap-2 hover:text-[#083A6B]">
                    <Phone size={12} /> {pousada.telefone}
                  </a>
                )}
                {pousada.email && (
                  <a href={`mailto:${pousada.email}`} className="flex items-center gap-2 hover:text-[#083A6B]">
                    <Mail size={12} /> {pousada.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comodidades */}
      {Array.isArray(pousada.comodidades) && pousada.comodidades.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 py-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-4">
            Comodidades da pousada
          </div>
          <ChipsComodidades items={pousada.comodidades} scope="pousada" />
        </section>
      )}

      {/* Descrição */}
      {pousada.descricao && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-4">
                Sobre a pousada
              </div>
              <div className="font-public-title text-stone-800 text-lg lg:text-xl leading-relaxed whitespace-pre-line">
                {pousada.descricao}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quartos */}
      <section id="quartos" data-testid="pousada-quartos" className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20 bg-[#FFFFFF] border-y border-stone-200 -mx-5 lg:mx-0 lg:rounded-lg px-5 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-3">
              Hospedagem
            </div>
            <h2 className="font-public-title text-3xl lg:text-4xl text-stone-900 leading-tight">
              Nossos quartos
            </h2>
          </div>

          {quartos.length === 0 ? (
            <div className="text-center py-14 text-stone-500 text-sm">
              Quartos serão exibidos em breve.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {quartos.map((q) => (
                <QuartoCard key={q.id} quarto={q} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Final */}
      <section className="max-w-5xl mx-auto px-5 lg:px-10 py-14 lg:py-20 text-center">
        <h3 className="font-public-title text-2xl lg:text-3xl text-stone-900 mb-4">
          Pronto para reservar na {pousada.nome}?
        </h3>
        <p className="text-stone-600 mb-7 max-w-xl mx-auto">
          Fale com nosso time pelo WhatsApp. Confirmamos disponibilidade e condições reais em minutos.
        </p>
        <CTAWhatsApp variant="primary" context={ctxWa} label="Reservar via WhatsApp" testid="pousada-cta-final" />
      </section>
    </>
  );
}
